/* eslint-disable no-lonely-if */
/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable no-console */
/* eslint-disable max-len */
import { toast } from 'react-toastify';
import { createImageCollection, updateImage } from '../../../../../../../actions/image_management';
import { CreateImagePayload, ImageFile } from '../../../../../../../actions/image_management/image_management';
import { newNewsFeedUploadFiles } from '../../../../../../../../libs/HelperContent';

// Simplified interfaces
export interface AccessControl {
      internalEmployees?: {
        allSelected?: boolean;
        departments?: Array<{name: string; count?: number}>;
        specificUsers?: Array<{userId: string; name: string; email: string; role?: string}>;
      };
      userGroups?: Array<{type: string; count?: number}>;
      participantCount?: number;
      participantLimit?: number;
}

export interface ImageMetadata {
	title?: string;
	description?: string;
	categoryIds?: string[];
	tags?: string[];
	// visibility?: 'private' | 'public';
	status?: 'draft' | 'published' | 'scheduled' | 'unpublished';
	allowDownload?: boolean;
	allowShare?: boolean;
	// accessControl?: AccessControl;
}

export interface ImageUpdateOptions extends ImageMetadata {
	imageId: string;
	sourceType?: 'system' | 'user' | 'external' | 'media_drive' | 'embed_link';
}

export interface ImageUrlData extends ImageMetadata {
	url: string;
	fileType: string;
	thumbnail?: string;
	platform?: string;
	metadata?: {
		isDataUri?: boolean;
		sizeInBytes?: number;
		dimensions?: {
			width: number;
			height: number;
		};
	};
	requestFrom?: string;
}

// Constants
const DEFAULT_DIMENSIONS = { width: 800, height: 600 };
const DEFAULT_ACCESS_CONTROL: AccessControl = {
	internalEmployees : {
		allSelected   : true,
		departments   : [],
		specificUsers : []
	},
	userGroups       : [],
	participantCount : 0,
	participantLimit : 0
};

// For UpdateImage compatibility
interface ExtendedImagePayload {
	sourceType?: 'system' | 'user' | 'external' | 'media_drive' | 'embed_link';
	files?: any[];
  [key: string]: any;
}

/**
 * Get image dimensions with timeout and error handling
 */
const getImageDimensions = (source: string | File): Promise<{ width: number; height: number }> =>
{
	return new Promise((resolve) =>
	{
		const img = new Image();
		const timeout = setTimeout(() => resolve(DEFAULT_DIMENSIONS), 3000);

		img.onload = () =>
		{
			clearTimeout(timeout);
			resolve({ width: img.width, height: img.height });
		};

		img.onerror = () =>
		{
			clearTimeout(timeout);
			resolve(DEFAULT_DIMENSIONS);
		};

		if (source instanceof File)
		{
			img.src = URL.createObjectURL(source);
		}
		else
		{
			img.crossOrigin = 'anonymous';
			img.src = source;
		}
	});
};

/**
 * Create standardized image file payload
 */
const createImageFilePayload = (
	url: string,
	metadata: ImageMetadata & {
		fileType?: string;
		fileSize?: number;
		dimensions?: { width: number; height: number };
		thumbnail?: string;
		platform?: string;
	}
): ImageFile => ({
	title       : metadata.title || `Image-${Date.now()}`,
	description : metadata.description || '',
	url,
	thumbnail   : {
		url       : metadata.thumbnail || url,
		canEdit   : true,
		canDelete : false
	},
	fileType      : metadata.fileType || 'jpeg',
	fileSize      : metadata.fileSize || 0,
	dimensions    : metadata.dimensions || DEFAULT_DIMENSIONS,
	order         : 0,
	uploadedAt    : new Date().toISOString(),
	categoryIds   : metadata.categoryIds || [],
	tags          : metadata.tags || [],
	status        : metadata.status || 'draft',
	allowDownload : metadata.allowDownload ?? false,
	allowShare    : metadata.allowShare ?? false,
	...(metadata.platform && { platform: metadata.platform })
});

/**
 * Create image collection with unified error handling
 */
const createImageWithToast = async (
	payload: CreateImagePayload,
	loadingMessage: string,
	successMessage: string
): Promise<any> =>
{
	const toastId = toast.info(loadingMessage, { autoClose: false });

	try
	{
		console.log('Sending payload to API:', JSON.stringify(payload, null, 2));
		const response = await createImageCollection(payload);

		toast.update(toastId, {
			render    : successMessage,
			type      : toast.TYPE.SUCCESS,
			autoClose : 3000
		});

		return response;
	}
	catch (error)
	{
		console.error('API call failed:', error);
		toast.update(toastId, {
			render    : 'Failed to process image',
			type      : toast.TYPE.ERROR,
			autoClose : 5000
		});
		throw error;
	}
};

/**
 * Estimate data URI size in bytes
 */
const estimateDataUriSize = (dataUri: string): number =>
{
	if (!dataUri.includes(';base64,'))
	{
		return dataUri.length;
	}

	const base64Data = dataUri.split(';base64,')[1];

	if (!base64Data) return 0;

	// 4 base64 chars represent 3 bytes of data
	// Account for padding characters
	const paddingCount = (base64Data.match(/[=]/g) || []).length;

	return Math.floor((base64Data.length * 3) / 4) - paddingCount;
};

/**
 * Unified function to handle both file uploads and URL-based image processing
 * @param companyId - Company ID
 * @param source - Source type for the image
 * @param options - Either file upload options or URL options
 */
export const handleImageUpload = async (
	companyId: string,
	source: string,
	options: {
		// For file uploads
		file?: File;
		// For URL uploads
		url?: string;
		fileType?: string;
		platform?: string;
		thumbnail?: string;
		// Common metadata
		metadata?: ImageMetadata & {
			isDataUri?: boolean;
			sizeInBytes?: number;
			dimensions?: {
				width: number;
				height: number;
			};
		};
		// Optional flag for embed link requests
		requestFrom?: string;
	}
) =>
{
	try
	{
		const { file, url, fileType, platform, thumbnail, metadata = {}, requestFrom } = options;

		// Validate input - must have either file or url
		if (!file && !url)
		{
			throw new Error('Either file or URL must be provided');
		}
		if (file && url)
		{
			throw new Error('Cannot process both file and URL simultaneously');
		}
		let finalUrl = '';
		let finalFileType = '';
		let fileSize = 0;
		let fileTitle = '';
		let dimensions = DEFAULT_DIMENSIONS;
		let isDataUri = false;

		// Handle file upload path
		if (file)
		{
			// Upload to S3
			const uploadResult = await newNewsFeedUploadFiles([ file ], `${companyId}/resource`);
			const { name, url: uploadedUrl, type, size } = uploadResult[0];

			finalUrl = uploadedUrl;
			finalFileType = type.split('/')[1];
			fileSize = size;
			fileTitle = metadata.title || name || file.name.replace(/\.[^/.]+$/, '');

			// Get dimensions for file
			try
			{
				dimensions = await getImageDimensions(file);
			}
			catch (dimError)
			{
				console.warn('Could not determine image dimensions, using defaults', dimError);
			}
		}
		// Handle URL upload path
		else if (url)
		{
			isDataUri = url.startsWith('data:image/');

			finalUrl = url;

			// Extract file type from URL or data URI
			if (isDataUri)
			{
				const matches = url.match(/^data:image\/([a-z0-9.+-]+);base64,/i);

				finalFileType = (matches && matches[1]) ? matches[1].toLowerCase() : 'png';

				// Calculate data URI size
				if (!metadata.sizeInBytes)
				{
					fileSize = estimateDataUriSize(url);
				}
				else
				{
					fileSize = metadata.sizeInBytes;
				}
			}
			else
			{
				// Handle complex URLs, especially Google images
				if (fileType)
				{
					// If fileType is already provided, use it
					finalFileType = fileType;
				}
				else
				{
					// For Google encrypted thumbnails and complex URLs, default to jpg
					if (url.includes('encrypted-tbn') ||
						url.includes('googleusercontent.com') ||
						(url.includes('google.com') && (
							url.includes('&q=') ||
							url.includes('=pa-nu-') ||
							url.includes('=e365-') ||
							url.includes('=w')
						)))
					{
						finalFileType = 'jpg';
					}
					else
					{
						// Try to extract from URL patterns
						const urlObj = new URL(url);
						const extension = urlObj.pathname.split('.').pop()
							?.toLowerCase();

						if (extension && [ 'jpg', 'jpeg', 'png', 'gif', 'webp' ].includes(extension))
						{
							finalFileType = extension === 'jpeg' ? 'jpg' : extension;
						}
						else
						{
							// Default for all other URLs
							finalFileType = 'jpg';
						}
					}
				}
			}

			fileTitle = metadata.title || `Image-${Date.now()}`;

			// Use dimensions from metadata if provided
			// For external URLs, we skip dimension loading to avoid CORS issues
			if (metadata.dimensions)
			{
				dimensions = metadata.dimensions;
			}
			else
			{
				// For data URIs, try to get dimensions
				if (isDataUri)
				{
					try
					{
						dimensions = await getImageDimensions(url);
					}
					catch (dimError)
					{
						console.warn('Could not determine image dimensions, using defaults', dimError);
						dimensions = DEFAULT_DIMENSIONS;
					}
				}
				else
				{
					// For regular URLs, use default dimensions to avoid CORS issues
					dimensions = DEFAULT_DIMENSIONS;
				}
			}
		}

		// Determine source type based on input
		let sourceType: 'system' | 'media_drive' | 'user' | 'external' | 'embed_link';

		if (requestFrom === 'embedLink')
		{
			sourceType = 'embed_link'; // API documentation confirms this is correct
		}
		else if (file)
		{
			sourceType = source as 'system' | 'media_drive' | 'user' | 'external';
		}
		else
		{
			sourceType = 'system'; // Changed from 'embed_link' to 'system' for URL uploads
		}

		// Ensure file type is a supported format
		if (finalFileType === 'jpeg')
		{
			finalFileType = 'jpg';
		}

		if (![ 'jpg', 'png', 'gif', 'webp' ].includes(finalFileType))
		{
			finalFileType = 'jpg';
		}

		// Create unified file payload
		const filePayload = createImageFilePayload(finalUrl!, {
			...metadata,
			title     : fileTitle,
			fileType  : finalFileType,
			fileSize,
			dimensions,
			thumbnail : thumbnail || finalUrl,
			platform  : isDataUri ? 'data_uri_image' : (platform || 'image_url')
		});

		const payload: CreateImagePayload = {
			companyId,
			sourceType,
			files : [ filePayload ]
		};

		// Determine appropriate toast messages
		const loadingMessage = file ? 'Uploading image...' : 'Processing image URL...';
		const successMessage = file ? 'Image uploaded successfully!' : 'Image URL processed successfully!';

		return await createImageWithToast(payload, loadingMessage, successMessage);
	}
	catch (error)
	{
		console.error('Error in image processing:', error);

		// Provide contextual error messages
		const isFileUpload = Boolean(options.file);
		const baseMessage = isFileUpload ? 'Failed to upload image' : 'Failed to process image URL';

		let errorMessage = `${baseMessage}. Please try again.`;

		if (error instanceof Error)
		{
			if (error.message.includes('network'))
			{
				errorMessage = 'Network error. Please check your connection and try again.';
			}
			else if (error.message.includes('timeout'))
			{
				errorMessage = 'Request timed out. Please try again.';
			}
			else if (error.message.includes('permission'))
			{
				errorMessage = 'Permission denied. You may not have access to add images.';
			}
			else
			{
				errorMessage = error.message;
			}
		}

		toast.error(errorMessage);
		throw error;
	}
};

/**
 * Upload image URL
 */
export const uploadImageURL = async (
	companyId: string,
	imageData: ImageUrlData
): Promise<any> =>
{
	return handleImageUpload(companyId, 'system', {
		url       : imageData.url,
		fileType  : imageData.fileType,
		platform  : imageData.platform,
		thumbnail : imageData.thumbnail,
		metadata  : {
			...imageData,
			isDataUri   : imageData.url.startsWith('data:image/'),
			sizeInBytes : imageData.metadata?.sizeInBytes,
			dimensions  : imageData.metadata?.dimensions
		},
		requestFrom : imageData.requestFrom
	});
};

/**
 * Internal helper to update an image without showing toasts
 * (Used by handleMultipleImageUpdates to avoid duplicate toasts)
 */
const updateImageSilent = async (
	imageId: string,
	options: any
): Promise<any> =>
{
	// Define top-level keys
	const topLevelKeys = [ 'sourceType' ];

	// Define file-specific detail keys
	const fileDetailKeys = [
		'title', 'description', 'url', 'fileType', 'fileSize',
		'dimensions', 'order', 'categoryIds', 'tags', 'status',
		'allowDownload', 'allowShare',
		'quality', 'publishDate'
	];

	// Initialize the update payload with top-level keys
	const updatePayload: ExtendedImagePayload = {
		sourceType : options.sourceType || 'system'
	};

	// Process files if they exist
	if (options && options?.files && options?.files?.length > 0)
	{
		const file = options.files[0]; // Get the first file object

		// Build file details object using reduce
		const fileDetails = fileDetailKeys.reduce((acc: any, key) =>
		{
			if (file[key] !== undefined && file[key] !== null && file[key] !== '')
			{
				acc[key] = file[key];
			}

			return acc;
		}, {});

		// Handle thumbnail specially (due to nested structure)
		if (file.thumbnail?.url)
		{
			fileDetails.thumbnail = {
				url       : file.thumbnail.url,
				canEdit   : file.thumbnail.canEdit ?? true,
				canDelete : file.thumbnail.canDelete ?? false
			};
		}

		// Only add files array if there are details to update
		if (Object.keys(fileDetails).length > 0)
		{
			updatePayload.files = [ fileDetails ];
		}
	}

	// Call API to update image without toasts
	return await updateImage(imageId, updatePayload);
};

/**
 * Handles updating multiple images at once with the same data
 * 
 * @param companyId - The company ID
 * @param images - Array of image objects or IDs to update
 * @param updatedData - The data to apply to all images
 * @returns Promise with array of responses
 */
export const handleMultipleImageUpdates = async (
	companyId: string,
	images: any,
	updatedData: any,
	existingData: any
): Promise<any> =>
{
	if (!images || images.length === 0)
	{
		throw new Error('No image IDs provided for update');
	}

	try
	{
		const isBatchUpdate = images.length > 1;
		const imageItem = images[0]; // We're now processing one image at a time
		let imageId;

		if (typeof imageItem === 'string')
		{
			imageId = imageItem;
		}
		else if (imageItem.id)
		{
			imageId = imageItem.id;
		}
		else if (imageItem._id)
		{
			imageId = imageItem._id;
		}
		else if (typeof imageItem === 'object' && imageItem.files && imageItem.files[0])
		{
			imageId = imageItem.files[0].imageId;
		}
		if (!imageId)
		{
			console.error('Could not extract image ID from:', imageItem);
			throw new Error('Invalid image ID');
		}

		// Get the current item's data
		const currentItemData = existingData;
		// const existingCategories = currentItemData?.categoryIds || currentItemData?.categories || (currentItemData?.files && currentItemData.files[0]?.categoryIds) || [];
		const updatedCategories = updatedData.categoryIds || updatedData.categories || [];

		// // Helper function to compare category arrays
		// const areCategoriesDifferent = (updated: string[], existing: string[]) =>
		// {
		// 	if (!updated || !existing) return false;
		// 	if (updated.length !== existing.length) return true;

		// 	return !updated.every((cat) => existing.includes(cat));
		// };

		// Only include categories if they're different from existing ones
		// const shouldUpdateCategories = areCategoriesDifferent(updatedCategories, existingCategories);

		const mergedTags = isBatchUpdate
			? Array.from(new Set([
				...(currentItemData?.tags || []),
				...(updatedData.tags || [])
			]))
			: updatedData.tags;

		// Create a file object with all appropriate properties
		const fileDetails: any = {
			imageId,
			...(updatedData.title && { title: updatedData.title ?? currentItemData?.title }),
			...(updatedData.description && { description: updatedData.description ?? currentItemData?.description }),
			...(updatedData.url && { url: updatedData.url ?? currentItemData?.url }),
			...(updatedData.tags && { tags: mergedTags }),
			...(updatedData.status && { status: updatedData.status ?? currentItemData?.status }),
			...(updatedData.hasOwnProperty('allowDownload') && { allowDownload: updatedData.allowDownload ?? currentItemData?.allowDownload }),
			...(updatedData.hasOwnProperty('allowShare') && { allowShare: updatedData.allowShare ?? currentItemData?.allowShare }),
			...(updatedData.quality && { quality: updatedData.quality ?? currentItemData?.quality }),
			...(updatedData.publishDate && { publishDate: updatedData.publishDate ?? currentItemData?.publishDate })
		};

		if (updatedCategories?.length > 0 && updatedCategories?.[0] !== currentItemData?.categoryIds?.[0])
		{
			fileDetails.categoryIds = updatedCategories;
		}
		// Add thumbnail if provided
		if (updatedData.thumbnail?.url || currentItemData?.thumbnail?.url)
		{
			fileDetails.thumbnail = {
				url       : updatedData.thumbnail?.url ?? currentItemData?.thumbnail?.url,
				canEdit   : updatedData.thumbnail?.canEdit ?? currentItemData?.thumbnail?.canEdit ?? true,
				canDelete : updatedData.thumbnail?.canDelete ?? currentItemData?.thumbnail?.canDelete ?? false
			};
		}

		// Remove undefined fields
		Object.keys(fileDetails).forEach((key) =>
		{
			if (fileDetails[key] === undefined)
			{
				delete fileDetails[key];
			}
		});

		// Create proper options structure for updateImageSilent
		const options = {
			files      : [ fileDetails ],
			sourceType : 'system'
		};

		const response = await updateImageSilent(imageId, options);

		const refreshEvent = new CustomEvent('contentStatusChanged', {
			detail : {
				contentId   : imageId,
				contentType : 'Images',
				status      : 'updated'
			}
		});

		window.dispatchEvent(refreshEvent);

		return response;
	}
	catch (error)
	{
		console.error('Error updating image:', error);
		throw error;
	}
};